import type {ImageField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import cn from 'classnames'
import Link from 'next/link'
import {Icon} from 'src/common/Icon'
import {DEFAULT_PADDING, PADDING_BOTTOM_CLASS, PADDING_TOP_CLASS} from 'src/common/paddingClasses'
import {StarRating} from 'src/common/StarRating'

type Review = {
  badgeIcon: ImageField
  source: string
  rating: string
  description: string
}

type Customer = {
  companyName: string
  companyLogo: ImageField
  heroImage: ImageField
  url: string | null
}

type FeaturedCustomer = {
  companyLogo: ImageField
  image: ImageField
  url: string | null
} | null

export type SocialProofBarUIProps = {
  heading: string
  featuredCustomer: FeaturedCustomer
  featuredStatNumber: string
  featuredStatDescription: string
  bottomLabel: string
  reviews: Review[]
  customers: Customer[]
  paddingTop?: string | null
  paddingBottom?: string | null
}

const ReviewBadge = ({badgeIcon, source, rating, description}: Review) => (
  <div className="flex w-full items-center justify-between gap-3 lg:w-auto lg:justify-start">
    <div className="flex items-center gap-3">
      {isFilled.image(badgeIcon) && (
        <PrismicNextImage field={badgeIcon} width={40} height={40} className="size-10 shrink-0 object-contain" />
      )}
      <div className="flex flex-col">
        {source && <p className="text-10 font-semibold uppercase tracking-[0.55px] text-moss-white/70">{source}</p>}
        {description && <p className="max-w-[140px] text-15 leading-5 text-moss-white">{description}</p>}
      </div>
    </div>
    <div className="flex items-center gap-1">
      {rating && <p className="font-main text-22 text-moss-white">{rating}</p>}
      <StarRating rating={5} color="White" />
    </div>
  </div>
)

export const DefaultSocialProofBar = ({
  heading,
  featuredCustomer,
  featuredStatNumber,
  featuredStatDescription,
  bottomLabel,
  reviews,
  customers,
  paddingTop,
  paddingBottom,
}: SocialProofBarUIProps) => {
  const paddingTopClass = PADDING_TOP_CLASS[paddingTop ?? 'None'] ?? ''
  const paddingBottomClass = PADDING_BOTTOM_CLASS[paddingBottom ?? DEFAULT_PADDING] ?? ''

  return (
    <section
      data-slice-type="social_proof_bar"
      data-slice-variation="default"
      data-nav-dark="true"
      className={cn(
        'z-[11] -mt-px bg-green-dark px-5 pt-px md:px-10 lg:px-5 xl:px-0',
        paddingTopClass,
        paddingBottomClass,
      )}
    >
      {heading && (
        <p className="mx-auto max-w-container font-main text-24 leading-tight text-moss-white lg:text-32">{heading}</p>
      )}
      <div className="relative z-20 mx-auto mt-6 w-full max-w-container overflow-hidden rounded-lg border border-moss-white/30 bg-[rgba(52,81,66,0.2)] backdrop-blur-md">
        <div className="grid grid-cols-2 lg:grid-cols-[300px_repeat(3,1fr)]">
          {featuredCustomer && (
            <div className="group/featured relative col-span-2 flex h-[320px] flex-col justify-end overflow-hidden border-b border-moss-white/30 lg:col-span-1 lg:row-span-2 lg:h-auto lg:border-b-0 lg:border-r">
              {featuredCustomer.url && (
                <Link href={featuredCustomer.url} className="absolute inset-0 z-20" aria-label="View customer story" />
              )}
              {isFilled.image(featuredCustomer.image) && (
                <PrismicNextImage field={featuredCustomer.image} fill className="object-cover" />
              )}
              {isFilled.image(featuredCustomer.companyLogo) && (
                <div className="absolute left-3 top-3 z-10 h-6 w-24">
                  <PrismicNextImage field={featuredCustomer.companyLogo} fill className="object-contain object-left" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#002414] to-transparent" />
              {featuredCustomer.url && (
                <span className="absolute right-3 top-3 z-10 flex size-7 items-center justify-center rounded-[3px] bg-moss-white/[0.05] backdrop-blur-sm transition-colors duration-300 group-hover/featured:bg-moss-white/[0.25]">
                  <Icon name="arrow" className="h-2 w-2.5 text-moss-white" aria-hidden="true" />
                </span>
              )}
              <div className="relative z-10 flex items-end gap-2 p-4">
                {featuredStatNumber && (
                  <p className="font-main text-40 leading-none tracking-[0.4px] text-green-neon">
                    {featuredStatNumber}
                  </p>
                )}
                {featuredStatDescription && (
                  <p className="max-w-[124px] font-main text-16 leading-tight text-moss-white">
                    {featuredStatDescription}
                  </p>
                )}
              </div>
            </div>
          )}
          {customers.map((customer, customerIndex) => {
            const isLastInMobileRow = customerIndex % 2 === 1
            const isLastInDesktopRow = customerIndex % 3 === 2
            const isBottomMobileRow = customerIndex >= customers.length - (customers.length % 2 || 2)
            const isBottomDesktopRow = customerIndex >= customers.length - (customers.length % 3 || 3)

            return (
              isFilled.image(customer.companyLogo) && (
                <div
                  key={customerIndex}
                  className={cn(
                    'group/customer relative flex h-[100px] items-center justify-center overflow-hidden border-moss-white/30 p-6',
                    isBottomMobileRow ? 'border-b-0' : 'border-b',
                    isBottomDesktopRow ? 'lg:border-b-0' : 'lg:border-b',
                    isLastInMobileRow ? 'border-r-0' : 'border-r',
                    isLastInDesktopRow ? 'lg:border-r-0' : 'lg:border-r',
                  )}
                >
                  {customer.url && (
                    <Link href={customer.url} className="absolute inset-0 z-20" aria-label={customer.companyName} />
                  )}
                  {isFilled.image(customer.heroImage) && (
                    <>
                      <PrismicNextImage
                        field={customer.heroImage}
                        fill
                        className="object-cover object-[center_25%] opacity-0 transition-opacity delay-75 duration-300 group-hover/customer:opacity-100 group-hover/customer:delay-0"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#002414] to-transparent opacity-0 transition-opacity duration-[900ms] group-hover/customer:opacity-100 group-hover/customer:duration-150" />
                    </>
                  )}
                  <PrismicNextImage
                    field={customer.companyLogo}
                    className="relative z-10 max-h-9 w-auto max-w-[130px] object-contain transition-all duration-300 group-hover/customer:translate-y-4"
                  />
                </div>
              )
            )
          })}
        </div>
        {(bottomLabel || reviews.length > 0) && (
          <div className="flex flex-col gap-4 border-t border-moss-white/30 p-5 lg:grid lg:grid-cols-[280px_1fr] lg:items-center">
            {bottomLabel && (
              <p className="text-12 font-semibold uppercase tracking-[0.72px] text-moss-white">{bottomLabel}</p>
            )}
            {reviews.length > 0 && (
              <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-center lg:justify-around">
                {reviews.map((review, reviewIndex) => (
                  <ReviewBadge key={reviewIndex} {...review} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
